import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  CalendarClock, Plus, Trash2, CalendarIcon, Check,
} from "lucide-react";
import { toast } from "sonner";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

const fmt = (ts) =>
  new Date(ts).toLocaleString([], {
    weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

export const ScheduleManager = ({ events, schedules, onCreate, onDelete, onClearCompleted }) => {
  const [open, setOpen] = useState(false);
  const [eventId, setEventId] = useState("");
  const [date, setDate] = useState(undefined);
  const [hour, setHour] = useState("09");
  const [minute, setMinute] = useState("00");
  const [dateOpen, setDateOpen] = useState(false);
  const [hasEnd, setHasEnd] = useState(false);
  const [endHour, setEndHour] = useState("18");
  const [endMinute, setEndMinute] = useState("00");
  const [fallbackId, setFallbackId] = useState("");

  const now = Date.now();

  const reset = () => {
    setEventId("");
    setDate(undefined);
    setHour("09");
    setMinute("00");
    setHasEnd(false);
    setEndHour("18");
    setEndMinute("00");
    setFallbackId("");
  };

  const submit = async () => {
    if (!eventId || !date) {
      toast.error("Pick an event and a date");
      return;
    }
    const when = new Date(date);
    when.setHours(Number(hour), Number(minute), 0, 0);
    if (when.getTime() <= now) {
      toast.error("Pick a time in the future");
      return;
    }
    let payload = { event_id: eventId, activate_at: when.toISOString() };
    if (hasEnd) {
      const end = new Date(date);
      end.setHours(Number(endHour), Number(endMinute), 0, 0);
      if (end.getTime() <= when.getTime()) {
        toast.error("End time must be after the start time");
        return;
      }
      payload.end_at = end.toISOString();
      if (fallbackId) payload.fallback_event_id = fallbackId;
    }
    try {
      await onCreate(payload);
      toast.success("Switch scheduled");
      setOpen(false);
      reset();
    } catch {
      toast.error("Couldn't schedule");
    }
  };

  const upcoming = schedules.filter((s) => !s.applied);
  const past = schedules.filter((s) => s.applied);

  return (
    <section data-testid="schedule-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-extrabold tracking-tighter text-muted-foreground uppercase">
          Scheduled switches
        </h2>
        <div className="flex items-center gap-2">
          {past.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-muted-foreground active:scale-95 transition-transform duration-150"
              onClick={onClearCompleted}
              data-testid="schedule-clear-completed-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Clear completed
            </Button>
          )}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button
              className="rounded-full active:scale-95 transition-transform duration-150"
              disabled={!events.length}
              data-testid="schedule-create-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl" data-testid="schedule-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">
                Schedule a switch
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Event to go Live</Label>
                <Select value={eventId} onValueChange={setEventId}>
                  <SelectTrigger data-testid="schedule-event-select">
                    <SelectValue placeholder="Choose event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`schedule-event-option-${e.id}`}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start rounded-md font-normal"
                      data-testid="schedule-date-btn"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {date ? date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setDateOpen(false); }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Time</Label>
                <div className="flex items-center gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="flex-1" data-testid="schedule-hour-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="font-display font-bold">:</span>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="flex-1" data-testid="schedule-minute-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">Uses your device's local time.</p>
              </div>

              <div className="space-y-2 rounded-xl border border-border p-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="checkbox" data-testid="schedule-hasend-toggle" checked={hasEnd}
                    onChange={(e) => setHasEnd(e.target.checked)} className="accent-[#00C853]" />
                  Set an end time (auto-switch away)
                </label>
                {hasEnd && (
                  <div className="space-y-3 pt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-12">Ends at</span>
                      <Select value={endHour} onValueChange={setEndHour}>
                        <SelectTrigger className="flex-1" data-testid="schedule-endhour-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-56">{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                      <span className="font-display font-bold">:</span>
                      <Select value={endMinute} onValueChange={setEndMinute}>
                        <SelectTrigger className="flex-1" data-testid="schedule-endminute-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-56">{MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Then switch back to</Label>
                      <Select value={fallbackId} onValueChange={setFallbackId}>
                        <SelectTrigger data-testid="schedule-fallback-select"><SelectValue placeholder="Nothing (default greeting)" /></SelectTrigger>
                        <SelectContent>
                          {events.filter((e) => e.id !== eventId).map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                className="rounded-full active:scale-95 transition-transform duration-150"
                onClick={submit}
                data-testid="schedule-save-btn"
              >
                Schedule switch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {upcoming.length === 0 && past.length === 0 ? (
        <div
          data-testid="schedule-empty"
          className="rounded-2xl border border-dashed border-border bg-card p-8 text-center"
        >
          <CalendarClock className="h-6 w-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
          <p className="font-display font-bold tracking-tight">No schedules yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Plan ahead — e.g. auto-switch to your next event the morning it starts.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="schedule-list">
          {upcoming.map((s) => (
            <div
              key={s.id}
              data-testid={`schedule-row-${s.id}`}
              className="fade-up flex items-center justify-between rounded-xl border border-border bg-card p-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent/15 text-accent shrink-0">
                  <CalendarClock className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold tracking-tight truncate">{s.event_name}</p>
                  <p className="text-xs text-muted-foreground">
                    goes Live · {fmt(s.activate_at)}
                    {s.end_at ? ` → ends ${fmt(s.end_at)}${s.fallback_event_name ? `, then ${s.fallback_event_name}` : ""}` : ""}
                  </p>
                </div>
              </div>
              <Button
                data-testid={`schedule-delete-btn-${s.id}`}
                onClick={() => onDelete(s.id)}
                variant="outline"
                size="sm"
                className="rounded-full text-destructive hover:text-destructive active:scale-95 transition-transform duration-150 shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {past.map((s) => (
            <div
              key={s.id}
              data-testid={`schedule-row-${s.id}`}
              className="flex items-center justify-between rounded-xl border border-border bg-card/50 p-4 opacity-70"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground shrink-0">
                  <Check className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold tracking-tight truncate">{s.event_name}</p>
                  <p className="text-xs text-muted-foreground">switched · {fmt(s.activate_at)}</p>
                </div>
              </div>
              <Button
                data-testid={`schedule-delete-btn-${s.id}`}
                onClick={() => onDelete(s.id)}
                variant="ghost"
                size="sm"
                className="rounded-full text-muted-foreground hover:text-destructive active:scale-95 transition-transform duration-150 shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
